"use client";

import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { toast } from "@/utils/customToast";

type Props = {
  paymentId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => Promise<void> | void;
};

export default function DeletePaymentDialog({ paymentId, open, onOpenChange, onDeleted }: Props) {
  const { t } = useTranslation(["finances", "common"]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-foreground/80 h-fit max-h-dvh max-w-xl border-none p-0 backdrop-blur-xl">
        <ScrollArea className="h-full max-h-dvh">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-4">
              <DialogTitle className="text-accent-foreground flex items-center gap-3 text-xl font-semibold">
                <div className="bg-destructive/20 flex size-10 items-center justify-center rounded-xl backdrop-blur-sm">
                  <Icon
                    className="text-destructive"
                    height="1.25rem"
                    icon="mdi:trash-can"
                    width="1.25rem"
                  />
                </div>
                {t("finances:paymentsDetails.deleteRestore.deleteTitle", "Delete payment")}
              </DialogTitle>
            </DialogHeader>
            <div className="flex items-start gap-3">
              <div className="text-muted-foreground text-sm">
                <p>
                  {t(
                    "finances:paymentsDetails.deleteRestore.deleteDesc1",
                    "This will archive the payment so it no longer appears in your active lists.",
                  )}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                className="bg-muted/20 hover:bg-muted/30 border-border/50 rounded-xl"
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common:cancel", "Cancel")}
              </Button>
              <Button
                className="gap-2 rounded-xl"
                type="button"
                variant="destructive"
                onClick={async () => {
                  if (!paymentId) return;
                  try {
                    await axiosApiInstance.delete(
                      API_ENDPOINTS.TUTOR.FINANCES.PAYMENT_DELETE(paymentId),
                    );
                    toast.success(
                      t("finances:paymentsDetails.deleteRestore.deleteSuccess", "Payment deleted"),
                    );
                    await onDeleted?.();
                    onOpenChange(false);
                  } catch (err: any) {
                    const code = err?.response?.data?.error;
                    const msg =
                      (code &&
                        t(`finances:paymentsDetails.deleteRestore.deleteError.${code}` as any)) ||
                      t(
                        "finances:paymentsDetails.deleteRestore.deleteError.generic",
                        "Failed to delete payment. Please try again.",
                      );

                    toast.error(msg);
                  }
                }}
              >
                <Icon height="1rem" icon="mdi:trash-can" width="1rem" />
                {t("finances:paymentsDetails.deleteRestore.deleteCta", "Delete payment")}
              </Button>
            </div>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
